import {
  CORRECTION_REVIEW_DECISIONS,
  IDENTITY_EVIDENCE,
  RECORD_RELEASE_FORMATS,
  RECORDS_CORRECTION_AREAS,
  RECORDS_REQUEST_CHANNELS,
  RECORDS_REQUEST_KINDS,
  RECORDS_REQUEST_OUTCOMES,
  RECORDS_REQUEST_STATUSES,
  RECORDS_REQUESTER_RELATIONSHIPS,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { allergySummarySchema, personNameSchema } from './patients.ts';

// Medical records and data-subject rights (docs/plans/phase2-records-data-rights.md §3, §5).
// Request details, requester names, responses, recipients and reasons are PHI: they travel in
// bodies only, never in a URL, and are never logged.

export {
  IDENTITY_EVIDENCE,
  RECORD_RELEASE_FORMATS,
  RECORDS_CORRECTION_AREAS,
  RECORDS_REQUEST_CHANNELS,
  RECORDS_REQUEST_KINDS,
  RECORDS_REQUEST_OUTCOMES,
  RECORDS_REQUEST_STATUSES,
  RECORDS_REQUESTER_RELATIONSHIPS,
};

export const RECORDS_REQUEST_STATES = ['open', 'due_today', 'overdue', 'closed'] as const;
export const ACCESS_EVENT_KINDS = ['viewed', 'changed', 'printed', 'exported', 'other'] as const;

const freeText = z.string().trim().min(1).max(4000);
const shortText = z.string().trim().min(1).max(200);

const patientRefSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  patientNumber: z.string(),
});

// --- The register ------------------------------------------------------------------------------

export const createRecordsRequestBodySchema = z
  .object({
    clientRequestId: z.uuid(),
    kind: z.enum(RECORDS_REQUEST_KINDS),
    correctionArea: z.enum(RECORDS_CORRECTION_AREAS).optional(),
    requesterRelationship: z.enum(RECORDS_REQUESTER_RELATIONSHIPS),
    requesterName: shortText.optional(),
    channel: z.enum(RECORDS_REQUEST_CHANNELS),
    /** When the request arrived; not in the future (the server checks against its clock). */
    receivedAt: z.iso.datetime(),
    details: freeText,
  })
  .refine((b) => (b.kind === 'correction') === (b.correctionArea !== undefined), {
    message: 'Say whether the correction is to personal details or to the clinical record',
    path: ['correctionArea'],
  })
  .refine((b) => b.requesterRelationship === 'self' || b.requesterName !== undefined, {
    message: 'Give the name of the person asking for the patient',
    path: ['requesterName'],
  });
export type CreateRecordsRequestBody = z.infer<typeof createRecordsRequestBodySchema>;

export const verifyIdentityBodySchema = z
  .object({
    version: z.number().int().min(1),
    evidence: z.enum(IDENTITY_EVIDENCE),
    note: shortText.optional(),
    /** How the authority of someone asking for the patient was checked. */
    authorityNote: shortText.optional(),
  })
  .refine((b) => b.evidence !== 'other' || b.note !== undefined, {
    message: 'Say what was seen',
    path: ['note'],
  });
export type VerifyIdentityBody = z.infer<typeof verifyIdentityBodySchema>;

export const closeRecordsRequestBodySchema = z.object({
  version: z.number().int().min(1),
  outcome: z.enum(RECORDS_REQUEST_OUTCOMES),
  /** What the requester was told. */
  response: z.string().trim().min(5).max(4000),
});
export type CloseRecordsRequestBody = z.infer<typeof closeRecordsRequestBodySchema>;

export const linkChangeBodySchema = z.object({ auditEventId: z.uuid() });

/** Accept a correction (to be made as a normal amendment) or decline it, with a note. */
export const reviewCorrectionBodySchema = z.object({
  version: z.number().int().min(1),
  decision: z.enum(CORRECTION_REVIEW_DECISIONS),
  note: z.string().trim().min(5).max(2000),
});
export type ReviewCorrectionBody = z.infer<typeof reviewCorrectionBodySchema>;

export const recordsRequestListQuerySchema = z.object({
  state: z.enum(['open', 'closed']).default('open'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.uuid().optional(),
});

export const recordsRequestRowSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  patient: patientRefSchema,
  kind: z.enum(RECORDS_REQUEST_KINDS),
  receivedAt: z.iso.datetime(),
  dueOn: isoDaySchema,
  state: z.enum(RECORDS_REQUEST_STATES),
  status: z.enum(RECORDS_REQUEST_STATUSES),
  outcome: z.enum(RECORDS_REQUEST_OUTCOMES).nullable(),
  /** A verified correction nobody has reviewed yet (privacy tooling plan §5.2). */
  awaitingReview: z.boolean(),
});
export type RecordsRequestRow = z.infer<typeof recordsRequestRowSchema>;

// --- The access log ----------------------------------------------------------------------------

export const accessLogEntrySchema = z.object({
  id: z.uuid(),
  occurredAt: z.iso.datetime(),
  actor: actorSchema,
  action: z.string(),
  kind: z.enum(ACCESS_EVENT_KINDS),
  entityType: z.string().nullable(),
  entityId: z.uuid().nullable(),
  facility: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** The reason given with the event (break-glass, entered in error), if any. */
  reason: z.string().nullable(),
});
export type AccessLogEntry = z.infer<typeof accessLogEntrySchema>;

export const accessLogPageSchema = z.object({
  items: z.array(accessLogEntrySchema),
  nextCursor: z.uuid().nullable(),
});

// --- Releases -----------------------------------------------------------------------------------

export const createReleaseBodySchema = z.object({
  format: z.enum(RECORD_RELEASE_FORMATS),
  recipient: shortText,
  reason: reasonSchema,
});
export type CreateReleaseBody = z.infer<typeof createReleaseBodySchema>;

export const recordReleaseSchema = z.object({
  id: z.uuid(),
  requestId: z.uuid(),
  requestNumber: z.string(),
  patient: patientRefSchema,
  format: z.enum(RECORD_RELEASE_FORMATS),
  recipient: z.string(),
  reason: z.string(),
  contentHash: z.string(),
  sectionCounts: z.record(z.string(), z.number().int()),
  releasedAt: z.iso.datetime(),
  releasedBy: actorSchema,
});
export type RecordReleaseView = z.infer<typeof recordReleaseSchema>;

export const recordReleasePageSchema = z.object({
  items: z.array(recordReleaseSchema),
  nextCursor: z.uuid().nullable(),
});

// --- One request ------------------------------------------------------------------------------

export const recordsRequestSchema = recordsRequestRowSchema.extend({
  facilityId: z.uuid(),
  correctionArea: z.enum(RECORDS_CORRECTION_AREAS).nullable(),
  requesterRelationship: z.enum(RECORDS_REQUESTER_RELATIONSHIPS),
  requesterName: z.string().nullable(),
  channel: z.enum(RECORDS_REQUEST_CHANNELS),
  details: z.string(),
  identity: z
    .object({
      evidence: z.enum(IDENTITY_EVIDENCE),
      note: z.string().nullable(),
      authorityNote: z.string().nullable(),
      verifiedBy: actorSchema,
      verifiedAt: z.iso.datetime(),
    })
    .nullable(),
  response: z.string().nullable(),
  closedBy: actorSchema.nullable(),
  closedAt: z.iso.datetime().nullable(),
  /** Corrections: the review (docs/plans/golive-privacy-tooling.md §5.2), once made. */
  review: z
    .object({
      decision: z.enum(CORRECTION_REVIEW_DECISIONS),
      note: z.string(),
      reviewedBy: actorSchema,
      reviewedAt: z.iso.datetime(),
    })
    .nullable(),
  version: z.number().int(),
  receivedBy: actorSchema,
  /** The outcomes this request may be closed with now. */
  allowedOutcomes: z.array(z.enum(RECORDS_REQUEST_OUTCOMES)),
  releases: z.array(recordReleaseSchema),
  changes: z.array(accessLogEntrySchema),
});
export type RecordsRequestView = z.infer<typeof recordsRequestSchema>;

// --- The export (docs/compliance/record-export-format.md) --------------------------------------

const exportVisitSchema = z.object({
  id: z.uuid(),
  visitNumber: z.string(),
  type: z.string(),
  facility: z.string(),
  status: z.enum(['open', 'closed', 'cancelled']),
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
  diagnoses: z.array(
    z.object({
      display: z.string(),
      code: z.string().nullable(),
      codeSystem: z.string().nullable(),
      codeSystemVersion: z.string().nullable(),
      rank: z.string(),
      certainty: z.string(),
      diagnosedAt: z.iso.datetime(),
    }),
  ),
  vitals: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
      unit: z.string(),
      effectiveAt: z.iso.datetime(),
    }),
  ),
  notes: z.array(
    z.object({
      signedAt: z.iso.datetime().nullable(),
      author: z.string(),
      amended: z.boolean(),
      sections: z.array(z.object({ heading: z.string(), text: z.string() })),
    }),
  ),
  prescriptions: z.array(
    z.object({
      number: z.string(),
      signedAt: z.iso.datetime(),
      drug: z.string(),
      dose: z.string(),
      route: z.string(),
      frequency: z.string(),
      duration: z.string().nullable(),
      status: z.string(),
    }),
  ),
  labResults: z.array(
    z.object({
      test: z.string(),
      releasedAt: z.iso.datetime().nullable(),
      results: z.array(
        z.object({
          name: z.string(),
          value: z.string(),
          unit: z.string().nullable(),
          flag: z.string().nullable(),
        }),
      ),
    }),
  ),
});
export type ExportVisit = z.infer<typeof exportVisitSchema>;

export const exportedRecordSchema = z.object({
  patient: z.object({
    patientNumber: z.string(),
    name: personNameSchema,
    displayName: z.string(),
    sex: z.string(),
    birthDate: isoDaySchema.nullable(),
    birthDateEstimated: z.boolean(),
    identifiers: z.array(z.object({ system: z.string(), value: z.string() })),
    phones: z.array(z.string()),
    residence: z.string().nullable(),
  }),
  allergies: allergySummarySchema,
  problems: z.array(
    z.object({
      display: z.string(),
      code: z.string().nullable(),
      codeSystem: z.string().nullable(),
      clinicalStatus: z.string(),
      onsetDate: isoDaySchema.nullable(),
    }),
  ),
  visits: z.array(exportVisitSchema),
  documents: z.object({
    sickSheets: z.number().int(),
    referralLetters: z.number().int(),
    uploads: z.number().int(),
  }),
});
export type ExportedRecord = z.infer<typeof exportedRecordSchema>;

export const RECORD_EXPORT_FORMAT = 'wodi.record-export';

export const recordExportSchema = z.object({
  format: z.literal(RECORD_EXPORT_FORMAT),
  formatVersion: z.literal(1),
  generatedAt: z.iso.datetime(),
  facility: z.object({ name: z.string() }),
  /** The fixed notice on every export (plan §3.5, TODO(legal-verify) R2). */
  notice: z.string(),
  /** True when the visit list was cut at the export's limit. */
  truncated: z.boolean(),
  /** A FHIR R4 collection Bundle: Patient, Organization, Encounter, Condition. */
  fhir: z.record(z.string(), z.unknown()),
  record: exportedRecordSchema,
});
export type RecordExport = z.infer<typeof recordExportSchema>;

export const releaseResultSchema = z.object({
  release: recordReleaseSchema,
  export: recordExportSchema,
});
export type ReleaseResult = z.infer<typeof releaseResultSchema>;
